<template>
	<div>
		<div class="d-flex justify-content-between">
			<h1 class="page-header">{{ $t('reportStockReturn.pageTitle') }}</h1>
			<ts-breadcrumb :items="[
				{ text: $t('home'), href: '/' },
				{
					text: $t('reportStockReturn.pageTitle'),
					active: true
				}
			]" />
		</div>
		<div class="row">
			<div class="col-md-3">
				<div class="form-group">
					<ts-card>
						<ts-card-wrapper>
							<ts-loading-banner :loading="loading">
								<form>
									<fieldset>
										<legend>{{ $t('reportStockReturn.criteria') }}</legend>
										<div class="row">
											<div class="col-md-12">
												<div class="form-group">
													<label class="required">{{
														$t('reportStockReturn.branch')
													}}</label>
													<a-select v-model="model.branch_id" :options="branches"
														class="ts-w-full" @change="onChangeBranch"></a-select>
													<div class="validate" v-if="errors.has('branch_id')">
														{{ errors.first('branch_id') }}
													</div>
												</div>
											</div>
										</div>
										<div class="row">
											<div class="col-md-12">
												<div class="form-group">
													<label>{{
														$t('reportStockReturn.location')
													}}</label>
													<ts-loading-banner :loading="locationLoading">
														<a-select v-model="model.location_id" :options="locations"
															class="ts-w-full"></a-select>
													</ts-loading-banner>
												</div>
											</div>
										</div>
										<div class="row">
											<div class="col-md-12">
												<div class="form-group">
													<label>{{
														$t('reportStockReturn.product')
													}}</label>
													<a-select v-model="model.product_id" class="ts-w-full"
														:placeholder="$t('all')" showSearch :loading="productLoading"
														:show-arrow="false" @search="onProductSearch" mode="multiple"
														:filterOption="(input, option) =>
															option.componentOptions.children[0].text
																.toLowerCase()
																.indexOf(input.toLowerCase()) >= 0" :options="productLists"></a-select>
												</div>
											</div>
										</div>
										<div class="row">
											<div class="col-md-12">
												<div class="form-group">
													<label class="required">{{
														$t('reportStockReturn.dateRange')
													}}</label>
													<a-range-picker format="DD-MM-YYYY" :defaultValue="rangeDate"
														:defaultPickerValue="rangeDate" @change="dateRangeChange" :ranges="{
															Today: [moment(), moment()],
															'This Month': [
																moment().startOf('month'),
																moment().endOf('month')
															]
														}">
														<template slot="renderExtraFooter"></template>
													</a-range-picker>
													<div class="validate" v-if="errors.has('date_range')">
														{{ errors.first('date_range') }}
													</div>
												</div>
											</div>
										</div>
										<div class="row">
											<div class="col-md-6 ts-text-right">
												<ts-button outline color="success" class="btn-block"
													@click.prevent="exportExcel" :waiting="exporting"><i
														class="far fa-file-excel"></i>
													{{ $t('export') }}</ts-button>
											</div>
											<div class="col-md-6 ts-text-right">
												<ts-button color="danger" outline class="btn-block" @click.prevent="preview"
													:waiting="waiting"><i class="far fa-file-pdf"></i>
													{{ $t('preview') }}</ts-button>
											</div>
										</div>
									</fieldset>
								</form>
							</ts-loading-banner>
						</ts-card-wrapper>
					</ts-card>
				</div>
			</div>
			<div class="col-md-9">
				<ts-card>
					<ts-card-wrapper>
						<form>
							<fieldset>
								<legend>{{ $t('reportStockReturn.preview') }}</legend>
								<ts-preview-pdf v-model="waiting" :src="src" />
							</fieldset>
						</form>
					</ts-card-wrapper>
				</ts-card>
			</div>
		</div>
	</div>
</template>
  
<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
	name: 'reportStockReturn',
	data() {
		return {
			waiting: false,
			exporting: false,
			loading: false,
			locationLoading: false,
			productLoading: false,
			errors: new Errors(),
			locations: [],
			branches: [],
			products: [],
			src: '',
			rangeDate: [moment(), moment()],
			model: {
				location_id: null,
				product_id: undefined,
				branch_id: null
			}
		}
	},
	computed: {
		productLists() {
			if (!this.products) return [];

			return this.products.map((el) => ({
				value: el.product_id,
				label: el.product_name_en + '|' + el.barcode,
			}))
		},
	},
	created() {
		this.fetchLocation()
	},
	methods: {
		...mapActions('report/resource', ['getBranch', 'productProfileLocation']),
		moment,
		async fetchLocation() {
			this.loading = true
			await this.getBranch()
				.then(response => {
					this.branches = response.data.map(el => ({
						value: el.branch_id,
						label: el.branch_name_en
					}))
					this.model.branch_id = this.$store.state.authUser.user.default_branch.branch_id
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
					this.onChangeBranch()
				})
			let product = await this.productProfileLocation()
			this.products = product.data
		},
		onProductSearch: _.debounce(function (value) {
			this.productLoading = true
			this.productProfileLocation({
				filter: {
					search: value
				},
			})
				.then(response => {
					this.products = response.data
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.productLoading = false
				})
		}, 1000),
		onChangeBranch() {
			this.locationLoading = true
			this.locations = []
			this.model.location_id = ''
			this.$store
				.dispatch('report/resource/getLocation', {
					branch_id: [this.model.branch_id]
				})
				.then(response => {
					this.locations = response.data.map(el => ({
						value: el.location_id,
						label: el.location_name_en
					}))
				})
				.finally(() => {
					this.locationLoading = false
				})
		},
		preview() {
			this.errors = new Errors()
			this.waiting = true
			this.src = ''
			this.$store
				.dispatch('report/inventory/stockReturn', this.model)
				.then(response => {
					this.src = response.url
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.waiting = false
				})
		},
		exportExcel() {
			this.exporting = true
			this.errors = new Errors()

			this.$store
				.dispatch('report/inventory/stockReturnExcel', this.model)
				.then(response => {
					let filename = trim(
						response.headers['content-disposition']
							.substring(
								response.headers['content-disposition'].indexOf('filename')
							)
							.replace('filename=', ''),
						'"'
					)
					FileDownload(response.data, filename)
				})
				.catch(err => {
					let error = JSON.parse(
						String.fromCharCode.apply(null, new Uint8Array(err))
					)
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => (this.exporting = false))
		},
		dateRangeChange(rangeDate) {
			this.$store.commit(
				'report/inventory/SET_DATE_FROM',
				rangeDate[0].format('DD-MM-YYYY')
			)
			this.$store.commit(
				'report/inventory/SET_DATE_TO',
				rangeDate[1].format('DD-MM-YYYY')
			)
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.commit(
			'report/inventory/SET_DATE_FROM',
			moment().format('DD-MM-YYYY')
		)
		this.$store.commit(
			'report/inventory/SET_DATE_TO',
			moment().format('DD-MM-YYYY')
		)
		next()
	}
}
</script>
  